import React from 'react';
import { Formik, FieldArray, FormikProps } from 'formik';
import { Form, Input } from 'formik-antd';
import { Row, Col, Divider } from 'antd';
import * as Yup from 'yup';

import ContactsSection, {
  contactsValidationSchema,
} from 'components/modals/sections/ContactsSection';
import {
  TimesheetApproverFormValues,
  timesheetApproverValidationSchema,
} from './TimesheetApproverSection';
import { ExtraItemEnum } from 'enums';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import { getContactLabel } from 'utils/assignmentUtils';
import FormikEffect from 'components/formik/FormikEffect';
import {
  CustomerResponseModel,
  PreferredContactMethodEnum,
  PrimaryContactForEnum,
} from 'api/backofficeApiClient';
import TimeSheetApproverFieldArray from './TimesheetApproverFieldArray';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import BillingContactFieldArray from './BillingContactsFieldArray';
import {
  BillingContactFormValues,
  billingContactsValidationSchema,
  submitBillingContactsValidationSchema,
} from './BillingContactSection';
import { getFullName } from 'utils/personUtils';

import styles from '../FinancialInfo/FinancialInfo.module.less';

type Props = {
  formRef: any;
  customers: CustomerResponseModel[];
  selectedCustomer?: CustomerResponseModel;
  onChange: (formik: FormikProps<CustomerInfoFormValues>) => void;
  initialValues?: CustomerInfoFormValues;
  onCustomerChange?: (customer?: CustomerResponseModel) => void;
  isRenewOrEditRenewMode?: boolean;
  isRehireOrEditRehireMode?: boolean;
  isRehireWithNewCustomer?: boolean;
  isCustomersLoaded?: boolean;
  hasActiveAssignmentRestriction: boolean;
};

interface PrimaryContactFormValues {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone?: string;
  preferredContactMethod?: PreferredContactMethodEnum[];
  contactFor?: PrimaryContactForEnum[];
}

export interface CustomerInfoFormValues {
  generalInfo: {
    customer: string;
    bestContacts: string[];
    primaryContacts?: PrimaryContactFormValues;
    billingContacts: string[];
    newBillingContacts: BillingContactFormValues[];
  };
  supervisorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    phone: string;
  };
  timesheetApproverInfo: {
    sameAsSupervisor: boolean;
    notApplicable: boolean;
    timesheetApprovers: TimesheetApproverFormValues[];
  };
  timeBackupApprover: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

export const isPrimaryContactSectionRequired = (bestContacts: string[]) =>
  bestContacts.includes(ExtraItemEnum.Other);

export const isBillingContactSectionRequired = (billingContacts: string[]) =>
  billingContacts.includes(ExtraItemEnum.AddBillingContact);

const initialValidationSchema = {
  generalInfo: {
    customer: Yup.string(),
    bestContacts: Yup.array().of(Yup.string()),
    billingContacts: Yup.array().of(Yup.string()),
    newBillingContacts: Yup.array().when('billingContacts', {
      is: isBillingContactSectionRequired,
      then: Yup.array().of(Yup.object(billingContactsValidationSchema)),
    }),
    primaryContacts: Yup.object({}).when('bestContacts', {
      is: isPrimaryContactSectionRequired,
      then: Yup.object(contactsValidationSchema),
    }),
  },
  supervisorInfo: {
    firstName: Yup.string()
      .trim()
      .max(120, 'First name should be 120 characters maximum'),
    lastName: Yup.string()
      .trim()
      .max(120, 'Last name should be 120 characters maximum'),
    title: Yup.string()
      .trim()
      .max(120, 'Title should be 120 characters maximum'),
    email: Yup.string()
      .email('Email format is invalid')
      .max(120, 'Email should be 120 characters maximum'),
    phone: Yup.string()
      .trim()
      .max(120, 'Phone number should be 120 characters maximum'),
  },
  timesheetApproverInfo: {
    sameAsSupervisor: Yup.boolean(),
    notApplicable: Yup.boolean(),
    timesheetApprovers: Yup.array().of(
      Yup.object(timesheetApproverValidationSchema),
    ),
  },
  timeBackupApprover: {
    firstName: Yup.string()
      .trim()
      .max(120, 'First name should be 120 characters maximum'),
    lastName: Yup.string()
      .trim()
      .max(120, 'Last name should be 120 characters maximum'),
    email: Yup.string()
      .email('Email format is invalid')
      .max(120, 'Email should be 120 characters maximum'),
    phone: Yup.string()
      .trim()
      .max(120, 'Phone number should be 120 characters maximum'),
  },
};

const submitValidationSchema = Yup.object({
  generalInfo: Yup.object({
    ...initialValidationSchema.generalInfo,
    customer:
      initialValidationSchema.generalInfo.customer.required(
        'Field is required',
      ),
    bestContacts: initialValidationSchema.generalInfo.bestContacts.min(
      1,
      'Field is required',
    ),
    billingContacts: initialValidationSchema.generalInfo.billingContacts.min(
      1,
      'Field is required',
    ),
    newBillingContacts: Yup.array().when('billingContacts', {
      is: isBillingContactSectionRequired,
      then: Yup.array().of(Yup.object(submitBillingContactsValidationSchema)),
    }),
    primaryContacts: Yup.object({}).when('bestContacts', {
      is: isPrimaryContactSectionRequired,
      then: Yup.object({
        ...contactsValidationSchema,
        firstName:
          contactsValidationSchema.firstName.required('Field is required'),
        lastName:
          contactsValidationSchema.lastName.required('Field is required'),
        title: contactsValidationSchema.title.required('Field is required'),
        email: contactsValidationSchema.email.required('Field is required'),
      }),
    }),
  }),
  supervisorInfo: Yup.object({
    ...initialValidationSchema.supervisorInfo,
    firstName:
      initialValidationSchema.supervisorInfo.firstName.required(
        'Field is required',
      ),
    lastName:
      initialValidationSchema.supervisorInfo.lastName.required(
        'Field is required',
      ),
    title:
      initialValidationSchema.supervisorInfo.title.required(
        'Field is required',
      ),
    email:
      initialValidationSchema.supervisorInfo.email.required(
        'Field is required',
      ),
    phone:
      initialValidationSchema.supervisorInfo.phone.required(
        'Field is required',
      ),
  }),
  timesheetApproverInfo: Yup.object({
    ...initialValidationSchema.timesheetApproverInfo,
    timesheetApprovers: Yup.array()
      .of(Yup.object(timesheetApproverValidationSchema))
      .when('notApplicable', {
        is: (notApplicable: boolean) => !notApplicable,
        then: Yup.array().of(
          Yup.object({
            firstName:
              timesheetApproverValidationSchema.firstName.required(
                'Field is required',
              ),
            lastName:
              timesheetApproverValidationSchema.lastName.required(
                'Field is required',
              ),
            email:
              timesheetApproverValidationSchema.email.required(
                'Field is required',
              ),
            phone:
              timesheetApproverValidationSchema.phone.required(
                'Field is required',
              ),
          }),
        ),
      }),
  }),
  timeBackupApprover: Yup.object({
    ...initialValidationSchema.timeBackupApprover,
  }),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    onCustomerChange,
    customers,
    selectedCustomer,
    isRenewOrEditRenewMode,
    isRehireOrEditRehireMode,
    isRehireWithNewCustomer,
    isCustomersLoaded,
    hasActiveAssignmentRestriction,
  } = props;
  const areFieldsDisabled =
    isRenewOrEditRenewMode ||
    (isRehireOrEditRehireMode && !isRehireWithNewCustomer) ||
    hasActiveAssignmentRestriction;

  const initialValues: CustomerInfoFormValues = {
    generalInfo: {
      customer: '',
      bestContacts: [],
      billingContacts: [],
      newBillingContacts: [],
      primaryContacts: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        title: '',
        preferredContactMethod: [],
        contactFor: [],
      },
    },
    supervisorInfo: {
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      phone: '',
    },
    timesheetApproverInfo: {
      sameAsSupervisor: false,
      notApplicable: false,
      timesheetApprovers: [
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
        },
      ],
    },
    timeBackupApprover: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  };

  const handleCustomerChange = (
    customerId: string,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    // we need reset fields after customer change
    formik.setValues({
      ...initialValues,
      generalInfo: {
        ...initialValues.generalInfo,
        customer: customerId,
      },
    });

    const customer = customers.find(
      (customer) => customer.customerId === customerId,
    );

    onCustomerChange?.(customer);
  };

  const handleBestContactsDeselect = (
    deselectedValue: string,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    if (deselectedValue !== ExtraItemEnum.Other) return;

    formik.setFieldValue(
      'generalInfo.primaryContacts',
      initialValues.generalInfo.primaryContacts,
      false,
    );
  };

  const handleBillingContactsDeselect = (
    deselectedValue: string,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    if (deselectedValue !== ExtraItemEnum.AddBillingContact) return;

    formik.setFieldValue('generalInfo.newBillingContacts', [], false);
  };

  const handleBillingContactsSelect = (
    selectedValue: string,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    if (selectedValue !== ExtraItemEnum.AddBillingContact) return;

    formik.setFieldValue(
      'generalInfo.newBillingContacts',
      [{ firstName: '', lastName: '', email: '', phoneNumber: '' }],
      false,
    );
  };

  const handleTimeSheetApproverSameAsSupervisorChange = (
    e: any,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    if (e.target.checked) return;

    // Clear first timesheet approver info section
    formik.setFieldValue(
      'timesheetApproverInfo.timesheetApprovers[0].firstName',
      '',
      false,
    );
    formik.setFieldValue(
      'timesheetApproverInfo.timesheetApprovers[0].lastName',
      '',
      false,
    );
    formik.setFieldValue(
      'timesheetApproverInfo.timesheetApprovers[0].email',
      '',
      false,
    );
    formik.setFieldValue(
      'timesheetApproverInfo.timesheetApprovers[0].phone',
      '',
      false,
    );

    formik.setFieldTouched(
      'timesheetApproverInfo.timesheetApprovers[0].firstName',
      true,
      false,
    );
    formik.setFieldTouched(
      'timesheetApproverInfo.timesheetApprovers[0].lastName',
      true,
      false,
    );
    formik.setFieldTouched(
      'timesheetApproverInfo.timesheetApprovers[0].email',
      true,
      false,
    );
    formik.setFieldTouched(
      'timesheetApproverInfo.timesheetApprovers[0].phone',
      true,
      false,
    );

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const handleTimeSheetApproverNotApplicableChange = (
    e: any,
    formik: FormikProps<CustomerInfoFormValues>,
  ) => {
    if (e.target.checked) {
      // Clear timesheet approvers
      formik.setFieldValue(
        'timesheetApproverInfo.timesheetApprovers',
        [
          {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
          },
        ],
        false,
      );

      formik.setFieldValue(
        'timesheetApproverInfo.sameAsSupervisor',
        false,
        false,
      );
    } else {
      formik.setFieldTouched(
        'timesheetApproverInfo.timesheetApprovers[0].firstName',
        true,
        false,
      );
      formik.setFieldTouched(
        'timesheetApproverInfo.timesheetApprovers[0].lastName',
        true,
        false,
      );
      formik.setFieldTouched(
        'timesheetApproverInfo.timesheetApprovers[0].email',
        true,
        false,
      );
      formik.setFieldTouched(
        'timesheetApproverInfo.timesheetApprovers[0].phone',
        true,
        false,
      );
    }

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">General information</div>
              {areFieldsDisabled && (
                <div className={styles.hint}>
                  <i className="icon icon-info-filled" />
                  Customer info cannot be modified because of MBP Assignment ID
                  logic
                </div>
              )}
              <Row gutter={40} align="middle">
                <Col xs={24} md={12}>
                  <FormItem
                    name="generalInfo.customer"
                    label="Select customer *"
                    data-id="newAssignmentClientField"
                  >
                    <FormikSelectWithLoading
                      name="generalInfo.customer"
                      placeholder="Select customer"
                      onChange={(customer) =>
                        handleCustomerChange(customer, formik)
                      }
                      disabled={areFieldsDisabled}
                      isLoading={!isCustomersLoaded}
                      optionFilterProp="label"
                      showSearch
                    >
                      {customers.map((customer) => (
                        <FormikSelectWithLoading.Option
                          key={`select-client-${customer.customerId}`}
                          value={customer.customerId || ''}
                          label={`${customer.customerCompanyName || ''} ${
                            customer.additionalInvoiceName || ''
                          }`.trim()}
                        >
                          <div>
                            <div>{customer.customerCompanyName}</div>
                            {customer.additionalInvoiceName && (
                              <div className={styles.additionalInvoiceName}>
                                {customer.additionalInvoiceName}
                              </div>
                            )}
                          </div>
                        </FormikSelectWithLoading.Option>
                      ))}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem
                    name="generalInfo.bestContacts"
                    label="Primary contact *"
                  >
                    <FormikSelectWithLoading
                      mode="multiple"
                      name="generalInfo.bestContacts"
                      disabled={
                        !formik.values.generalInfo.customer ||
                        isRenewOrEditRenewMode
                      }
                      placeholder="Select primary contact"
                      onDeselect={(value: string) =>
                        handleBestContactsDeselect(value, formik)
                      }
                    >
                      {selectedCustomer?.primaryContacts?.map(
                        (primaryContact) => (
                          <FormikSelectWithLoading.Option
                            key={`select-primary-contact-${primaryContact.contactId}`}
                            value={primaryContact.contactId || ''}
                          >
                            {getContactLabel(primaryContact)}
                          </FormikSelectWithLoading.Option>
                        ),
                      )}
                      {selectedCustomer?.isOtherPrimaryContactAvailable && (
                        <FormikSelectWithLoading.Option
                          key="select-primary-other-contact"
                          value={ExtraItemEnum.Other}
                        >
                          {ExtraItemEnum.Other}
                        </FormikSelectWithLoading.Option>
                      )}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>

                <Col xs={24} md={12} />

                <Col xs={24} md={12}>
                  <FormItem
                    name="generalInfo.billingContacts"
                    label="Billing contact *"
                  >
                    <FormikSelectWithLoading
                      mode="multiple"
                      name="generalInfo.billingContacts"
                      placeholder="Select billing contact"
                      disabled={
                        !formik.values.generalInfo.customer ||
                        isRenewOrEditRenewMode
                      }
                      onDeselect={(value: string) =>
                        handleBillingContactsDeselect(value, formik)
                      }
                      onSelect={(value: string) => {
                        handleBillingContactsSelect(value, formik);
                      }}
                    >
                      {selectedCustomer?.billingContacts?.map(
                        (billingContact) => (
                          <FormikSelectWithLoading.Option
                            key={`select-billing-contact-${billingContact.contactId}`}
                            value={billingContact.contactId || ''}
                          >
                            {getFullName(
                              billingContact.firstName,
                              billingContact.lastName,
                            )}
                          </FormikSelectWithLoading.Option>
                        ),
                      )}
                      {(selectedCustomer?.billingContacts || []).length < 5 && (
                        <FormikSelectWithLoading.Option
                          key="select-billing-add-contact"
                          value={ExtraItemEnum.AddBillingContact}
                        >
                          {ExtraItemEnum.AddBillingContact}
                        </FormikSelectWithLoading.Option>
                      )}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              {isBillingContactSectionRequired(
                formik.values.generalInfo.billingContacts,
              ) && (
                <>
                  <div className="modal-title">Billing information</div>

                  <FieldArray
                    name="generalInfo.newBillingContacts"
                    render={(props) => (
                      <BillingContactFieldArray
                        {...props}
                        billingContacts={selectedCustomer?.billingContacts}
                      />
                    )}
                  />

                  <Divider className="modal-divider after-form" />
                </>
              )}

              {isPrimaryContactSectionRequired(
                formik.values.generalInfo.bestContacts,
              ) && (
                <>
                  <ContactsSection
                    isPhoneRequired={false}
                    title="Primary contact"
                    formNamePrefix="generalInfo.primaryContacts"
                    showPreferredContactMethod
                    showContactFor
                    preferredContactMethodRequired={false}
                    contactForRequired={false}
                  />

                  <Divider className="modal-divider after-form" />
                </>
              )}
              <div className="modal-title">Supervisor information</div>
              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="supervisorInfo.firstName"
                        label="First name *"
                      >
                        <Input
                          name="supervisorInfo.firstName"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        name="supervisorInfo.lastName"
                        label="Last name *"
                      >
                        <Input
                          name="supervisorInfo.lastName"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="supervisorInfo.email" label="Email *">
                        <Input
                          name="supervisorInfo.email"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter email"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        label="Phone number *"
                        name="supervisorInfo.phone"
                      >
                        <Input
                          name="supervisorInfo.phone"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter phone number"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="supervisorInfo.title" label="Title *">
                    <Input
                      name="supervisorInfo.title"
                      disabled={!formik.values.generalInfo.customer}
                      placeholder="Enter title"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>
              <Divider className="modal-divider after-form" />
              <div className="modal-title">Timesheet approver information</div>
              <Row gutter={40}>
                <Col xs={24}>
                  <FormItem
                    name="timesheetApproverInfo.sameAsSupervisor"
                    className="checkbox-wrapper"
                  >
                    <Checkbox
                      name="timesheetApproverInfo.sameAsSupervisor"
                      disabled={
                        !formik.values.generalInfo.customer ||
                        formik.values.timesheetApproverInfo.notApplicable
                      }
                      onChange={(e) =>
                        handleTimeSheetApproverSameAsSupervisorChange(e, formik)
                      }
                    >
                      The same as supervisor
                    </Checkbox>
                  </FormItem>
                </Col>

                <FieldArray
                  name="timesheetApproverInfo.timesheetApprovers"
                  component={TimeSheetApproverFieldArray as any}
                />

                <Col xs={24}>
                  <FormItem
                    name="timesheetApproverInfo.notApplicable"
                    className="checkbox-wrapper"
                  >
                    <Checkbox
                      name="timesheetApproverInfo.notApplicable"
                      onChange={(e) =>
                        handleTimeSheetApproverNotApplicableChange(e, formik)
                      }
                      disabled={!formik.values.generalInfo.customer}
                    >
                      Not applicable
                    </Checkbox>
                  </FormItem>
                </Col>
              </Row>
              <Divider className="modal-divider after-form" />
              <div className="modal-title">
                Timesheet backup approver information
              </div>
              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem
                        name="timeBackupApprover.firstName"
                        label="First name"
                      >
                        <Input
                          name="timeBackupApprover.firstName"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        name="timeBackupApprover.lastName"
                        label="Last name"
                      >
                        <Input
                          name="timeBackupApprover.lastName"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="timeBackupApprover.email" label="Email">
                        <Input
                          name="timeBackupApprover.email"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter email"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        label="Phone number"
                        name="timeBackupApprover.phone"
                      >
                        <Input
                          name="timeBackupApprover.phone"
                          disabled={!formik.values.generalInfo.customer}
                          placeholder="Enter phone number"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
